<template>
  <AppLayout :breadcrumbs="[{ label: 'Patients', link: '/patients' }]">
    <div class="layout-header">
      <div class="header-left">
        <SearchBar :modelValue="searchTerm" @update:modelValue="handleSearchChange" placeholder="Search patients..." />
        <FilterSelector :modelValue="selectedFilters" @update:modelValue="handleFilterChange" :field-options="fieldOptions" />
      </div>
      <div class="header-right">
        <button class="button icon-button" @click="openColumnSettingsModal" title="Column Settings">
          <CogIcon :size="18" />
        </button>
        <button v-if="currentUser && currentUser.isAdmin" class="button" @click="openExportModal" :disabled="totalPatients === 0">
          Export
        </button>
        <button class="button primary" @click="handleCreatePatient">
          Create Patient
        </button>
      </div>
    </div>

    <div class="layout-body">
      <CustomTable :columns="visibleColumnsFiltered" :rows="patients" :sticky-first-column="true" :loading="isLoading"
        :is-empty="patients.length === 0" :sort-by="sortBy" :sort-order="sortOrder" @row-click="handleRowClick" @sort="handleSort">
        <template #column-patientName="{ row }">
          <div class="patient-name-cell">
            <IconCircle :size="28" bgColor="var(--icon-patient-bg)" color="var(--icon-patient-color)" :initials="getInitials(row)" />
            <span class="name-text">{{ getFullName(row) }}</span>
          </div>
        </template>

        <template #column-dateOfBirth="{ row }">
          <span v-if="row.dateOfBirth" class="cell-value">{{ formatUtils.formatDate(row.dateOfBirth) }}</span>
          <span v-else class="no-data">No data</span>
        </template>

        <template #column-mobilePhone="{ row }">
          <span v-if="row.mobilePhone" class="cell-value">{{ formatUtils.formatPhoneNumber(row.mobilePhone) }}</span>
          <span v-else class="no-data">No data</span>
        </template>

        <template #column-homePhone="{ row }">
          <span v-if="row.homePhone" class="cell-value">{{ formatUtils.formatPhoneNumber(row.homePhone) }}</span>
          <span v-else class="no-data">No data</span>
        </template>

        <template #column-workPhone="{ row }">
          <span v-if="row.workPhone" class="cell-value">{{ formatUtils.formatPhoneNumber(row.workPhone) }}</span>
          <span v-else class="no-data">No data</span>
        </template>

        <template #column-email="{ row }">
          <span v-if="row.email" class="cell-value">{{ row.email }}</span>
          <span v-else class="no-data">No data</span>
        </template>

        <template #column-ehrId="{ row }">
          <span v-if="row.ehrId" class="cell-value">{{ row.ehrId }}</span>
          <span v-else class="no-data">Not connected</span>
        </template>

        <template #column-fullAddress="{ row }">
          <span v-if="getFullAddress(row)" class="cell-value">{{ getFullAddress(row) }}</span>
          <span v-else class="no-data">No data</span>
        </template>

        <template #column-totalSpend="{ row }">
          <span v-if="row.numTickets > 0" class="cell-value">
            {{ formatUtils.formatCurrency(row.amountCentsTotal) }}
          </span>
          <span v-else class="no-data">No tickets</span>
        </template>

        <template #column-collected="{ row }">
          <span v-if="row.numTickets > 0" class="cell-value">
            {{ formatUtils.formatCurrency(row.amountCentsCollected) }}
          </span>
          <span v-else class="no-data">No tickets</span>
        </template>

        <template #column-cancelled="{ row }">
          <span v-if="row.numTickets > 0" class="cell-value">
            {{ formatUtils.formatCurrency(row.amountCentsCancelled) }}
          </span>
          <span v-else class="no-data">No tickets</span>
        </template>

        <template #column-balance="{ row }">
          <span v-if="row.numTickets > 0" class="cell-value">{{ formatUtils.formatCurrency(row.amountCentsBalance)
            }}</span>
          <span v-else class="no-data">No tickets</span>
        </template>

        <template #column-enrolledInTrt="{ row }">
          <span v-if="row.isTrtPatient" class="cell-value">Yes</span>
          <span v-else class="no-data">No</span>
        </template>

        <template #column-dateLastTicket="{ row }">
          <span v-if="row.dateLastTicket" class="cell-value">{{ formatUtils.formatDate(row.dateLastTicket) }}</span>
          <span v-else class="no-data">No tickets</span>
        </template>

        <template #column-dateCreated="{ row }">
          <span class="cell-value">{{ formatUtils.formatDate(row.dateCreated) }}</span>
        </template>
      </CustomTable>
    </div>

    <div class="layout-footer">
      <PaginationControl :current-page="currentPage" :total-items="totalPatients" :items-per-page="itemsPerPage"
        @page-change="handlePageChange" />
    </div>

    <!-- Modals -->
    <transition name="modal-fade" mode="out-in">
      <div class="modal-background close-on-click" v-if="modal.open" @click="handleModalClick">
        <div class="modal-scroll close-on-click">
          <transition name="modal-pop" mode="out-in">
            <ExportDataModal
              v-if="modal.id === 'export'"
              entity-type="patients"
              :columns="columns"
              :current-page-count="patients.length"
              :total-count="totalPatients"
              :export-config="exportConfig"
              :is-exporting="isExporting"
              :export-progress="exportProgress"
              :error="exportError"
              @export="handleExport"
            />
            <ColumnSettingsModal
              v-else-if="modal.id === 'columnSettings'"
              :columns="columns"
              :storage-key="`shawncrm_columns_${practiceId}_patients`"
              :initial-visibility="columnVisibility"
              @update:visibility="handleColumnVisibilityChange"
            />
          </transition>
        </div>
      </div>
    </transition>
  </AppLayout>
</template>

<script>
import AppLayout from '../../components/app/AppLayout.vue';
import SearchBar from '../../components/common/SearchBar.vue';
import FilterSelector from '../../components/common/FilterSelector.vue';
import CustomTable from '../../components/common/CustomTable.vue';
import PaginationControl from '../../components/common/PaginationControl.vue';
import IconCircle from '../../components/common/IconCircle.vue';
import ExportDataModal from '../../modals/ExportDataModal.vue';
import ColumnSettingsModal from '../../modals/ColumnSettingsModal.vue';
import CogIcon from 'vue-material-design-icons/Cog.vue';
import { queryClient } from '@/libs/queryClient';
import { alertService } from '@/libs/alert';
import * as api from '@/api';
import * as formatUtils from '@/utils/formatters';
import { generateCSV, downloadCSV } from '@/utils/csvExport';
import { serializeFilters, deserializeFilters } from '@/utils/filterSerializer';
import { getLocalToday } from '@/utils/dateUtils';

export default {
  name: 'PatientListView',
  data() {
    return {
      searchTerm: '',
      itemsPerPage: 100,
      currentPage: 1,
      totalPatients: 0,
      patients: [],
      isLoading: false,
      selectedFilters: [],
      currentUser: null,
      columnVisibility: {},
      // Modal state
      modal: {
        open: false,
        id: '',
        data: {}
      },
      // Export state
      isExporting: false,
      exportProgress: { current: 0, total: 0 },
      exportError: null,
      fieldOptions: [
        {
          id: 'dateOfBirth',
          label: 'Date of Birth',
          type: 'date',
          apiParams: {
            from: 'dateOfBirthAfter',
            to: 'dateOfBirthBefore'
          }
        },
        {
          id: 'dateCreated',
          label: 'Date Created',
          type: 'date',
          apiParams: {
            from: 'dateCreatedAfter',
            to: 'dateCreatedBefore'
          }
        },
        {
          id: 'dateLastTicket',
          label: 'Date Last Ticket',
          type: 'date',
          apiParams: {
            from: 'dateLastTicketAfter',
            to: 'dateLastTicketBefore'
          }
        },
        {
          id: 'productIds',
          label: 'Products',
          type: 'multiselect',
          apiParams: {
            param: 'productIds',
            mode: 'productFilterMode'
          },
          options: []
        },
        {
          id: 'amountCentsTotal',
          label: 'Total Spend',
          type: 'currency',
          apiParams: {
            from: 'amountCentsTotalGte',
            to: 'amountCentsTotalLte'
          }
        },
        {
          id: 'amountCentsBalance',
          label: 'Current Balance',
          type: 'currency',
          apiParams: {
            from: 'amountCentsBalanceGte',
            to: 'amountCentsBalanceLte'
          }
        },
        {
          id: 'amountCentsCollected',
          label: 'Amount Collected',
          type: 'currency',
          apiParams: {
            from: 'amountCentsCollectedGte',
            to: 'amountCentsCollectedLte'
          }
        },
        {
          id: 'amountCentsCancelled',
          label: 'Amount Cancelled',
          type: 'currency',
          apiParams: {
            from: 'amountCentsCancelledGte',
            to: 'amountCentsCancelledLte'
          }
        },
        {
          id: 'isTrtPatient',
          label: 'TRT Enrollment',
          type: 'select',
          apiParams: {
            param: 'isTrtPatient'
          },
          options: [
            { value: 'true', label: 'Enrolled in TRT' },
            { value: 'false', label: 'Not enrolled in TRT' }
          ]
        }
      ],
      sortBy: 'dateCreated',
      sortOrder: 'desc',
      columns: [
        { key: 'patientName', label: 'Patient Name', width: '300px' },
        { key: 'dateOfBirth', label: 'Date of Birth', width: '200px' },
        { key: 'mobilePhone', label: 'Mobile Phone', width: '200px' },
        { key: 'homePhone', label: 'Home Phone', width: '200px' },
        { key: 'workPhone', label: 'Work Phone', width: '200px' },
        { key: 'email', label: 'Email', width: '200px' },
        { key: 'ehrId', label: 'EMR Integration ID', width: '300px' },
        { key: 'fullAddress', label: 'Full Address', width: '300px' },
        { key: 'totalSpend', label: 'Total Spend', width: '200px', sortable: true, sortKey: 'amountCentsTotal', description: 'Total amount from all tickets for this patient' },
        { key: 'collected', label: 'Amount Collected', width: '200px', sortable: true, sortKey: 'amountCentsCollected', description: 'Total amount successfully collected from payments' },
        { key: 'cancelled', label: 'Amount Cancelled', width: '200px', sortable: true, sortKey: 'amountCentsCancelled', description: 'Total amount cancelled from tickets and receivables' },
        { key: 'balance', label: 'Current Balance', width: '200px', sortable: true, sortKey: 'amountCentsBalance', description: 'Amount still owed: total initially owed minus amount paid minus amount cancelled' },
        { key: 'enrolledInTrt', label: 'Enrolled in TRT', width: '200px', description: 'Whether this patient is enrolled in the TRT program' },
        { key: 'dateLastTicket', label: 'Date Last Ticket', width: '200px', sortable: true, sortKey: 'dateLastTicket', description: 'Date of the most recent ticket created for this patient' },
        { key: 'dateCreated', label: 'Date Created', width: '200px', sortable: true, sortKey: 'dateCreated' }
      ],
      // Export column mapping
      exportConfig: {
        patientName: {
          label: 'Patient Name',
          format: (row) => `${row.firstName || ''} ${row.lastName || ''}`.trim() || 'Unknown'
        },
        dateOfBirth: {
          label: 'Date of Birth',
          format: (row) => row.dateOfBirth ? formatUtils.formatDate(row.dateOfBirth) : ''
        },
        mobilePhone: {
          label: 'Mobile Phone',
          format: (row) => row.mobilePhone ? formatUtils.formatPhoneNumber(row.mobilePhone) : ''
        },
        homePhone: {
          label: 'Home Phone',
          format: (row) => row.homePhone ? formatUtils.formatPhoneNumber(row.homePhone) : ''
        },
        workPhone: {
          label: 'Work Phone',
          format: (row) => row.workPhone ? formatUtils.formatPhoneNumber(row.workPhone) : ''
        },
        email: {
          label: 'Email',
          format: (row) => row.email || ''
        },
        ehrId: {
          label: 'EMR Integration ID',
          format: (row) => row.ehrId || ''
        },
        fullAddress: {
          label: 'Full Address',
          format: (row) => {
            const parts = [];
            if (row.addressStreet) parts.push(row.addressStreet);
            if (row.addressCity) parts.push(row.addressCity);
            if (row.addressState) parts.push(row.addressState);
            if (row.addressZip) parts.push(row.addressZip);
            return parts.join(', ');
          }
        },
        totalSpend: {
          label: 'Total Spend',
          format: (row) => row.numTickets > 0 ? formatUtils.formatCurrency(row.amountCentsTotal) : ''
        },
        collected: {
          label: 'Amount Collected',
          format: (row) => row.numTickets > 0 ? formatUtils.formatCurrency(row.amountCentsCollected) : ''
        },
        cancelled: {
          label: 'Amount Cancelled',
          format: (row) => row.numTickets > 0 ? formatUtils.formatCurrency(row.amountCentsCancelled) : ''
        },
        balance: {
          label: 'Current Balance',
          format: (row) => row.numTickets > 0 ? formatUtils.formatCurrency(row.amountCentsBalance) : ''
        },
        enrolledInTrt: {
          label: 'Enrolled in TRT',
          format: (row) => row.isTrtPatient ? 'Yes' : 'No'
        },
        dateLastTicket: {
          label: 'Date Last Ticket',
          format: (row) => row.dateLastTicket ? formatUtils.formatDate(row.dateLastTicket) : ''
        },
        dateCreated: {
          label: 'Date Created',
          format: (row) => formatUtils.formatDate(row.dateCreated)
        }
      },
      formatUtils
    };
  },
  computed: {
    practiceId() {
      return api.getPracticeId();
    },
    visibleColumnsFiltered() {
      return this.columns.filter(col => {
        // Always show first column (patientName)
        if (col.key === 'patientName') return true;
        // Show column if visibility is not set (default true) or explicitly true
        return this.columnVisibility[col.key] !== false;
      });
    }
  },
  watch: {
    '$route.query': {
      handler(newQuery, oldQuery) {
        // Only handle if query actually changed (browser back/forward navigation)
        // Skip if it's just our own syncStateToUrl() update
        if (JSON.stringify(newQuery) === JSON.stringify(oldQuery)) return;

        // Check if any values actually differ from current state
        const urlFilters = deserializeFilters(newQuery.filters);
        const urlSearch = newQuery.search || '';
        const urlPage = parseInt(newQuery.page) || 1;
        const urlSortBy = newQuery.sortBy || 'dateCreated';
        const urlSortOrder = newQuery.sortOrder || 'desc';

        const hasChanges =
          JSON.stringify(urlFilters) !== JSON.stringify(this.selectedFilters) ||
          urlSearch !== this.searchTerm ||
          urlPage !== this.currentPage ||
          urlSortBy !== this.sortBy ||
          urlSortOrder !== this.sortOrder;

        if (!hasChanges) return;

        // Update state from URL
        this.selectedFilters = urlFilters;
        this.searchTerm = urlSearch;
        this.currentPage = urlPage;
        this.sortBy = urlSortBy;
        this.sortOrder = urlSortOrder;

        this.loadPatients();
      }
    }
  },
  async created() {
    // Check if URL has any state params
    const hasUrlParams =
      this.$route.query.filters !== undefined ||
      this.$route.query.search !== undefined ||
      this.$route.query.sortBy !== undefined ||
      this.$route.query.sortOrder !== undefined;

    if (hasUrlParams) {
      // Load from URL (existing logic)
      if (this.$route.query.filters !== undefined) {
        this.selectedFilters = deserializeFilters(this.$route.query.filters);
      }
      if (this.$route.query.search !== undefined) {
        this.searchTerm = this.$route.query.search || '';
      }
      if (this.$route.query.sortBy !== undefined) {
        this.sortBy = this.$route.query.sortBy || 'dateCreated';
      }
      if (this.$route.query.sortOrder !== undefined) {
        this.sortOrder = this.$route.query.sortOrder || 'desc';
      }
    } else {
      // Load from localStorage
      const savedState = this.loadFiltersFromLocalStorage();
      if (savedState) {
        this.selectedFilters = savedState.selectedFilters || [];
        this.sortBy = savedState.sortBy || 'dateCreated';
        this.sortOrder = savedState.sortOrder || 'desc';

        // Immediately sync to URL
        this.syncStateToUrl();
      }
    }

    this.currentPage = parseInt(this.$route.query.page) || 1;

    // Load column visibility settings
    this.loadColumnVisibility();

    this.currentUser = queryClient.getQueryData(['user']) || null;
    await this.loadFilterOptions();
    this.loadPatients();
  },
  methods: {
    async loadFilterOptions() {
      try {
        const productsResponse = await queryClient.fetchQuery({
          queryKey: ['products', { includeDeleted: true }],
          queryFn: () => api.fetchProducts({ includeDeleted: true })
        });

        const productField = this.fieldOptions.find(f => f.id === 'productIds');
        if (productField && productsResponse.products) {
          productField.options = productsResponse.products.map(product => ({
            value: product.productId,
            label: product.isDeleted ? `${product.name} (deleted)` : product.name
          }));
        }
      } catch (err) {
        console.error('Failed to load filter options:', err);
      }
    },
    syncStateToUrl() {
      const query = { ...this.$route.query };

      // Sync filters
      const serialized = serializeFilters(this.selectedFilters);
      if (serialized) {
        query.filters = serialized;
      } else {
        delete query.filters;
      }

      // Sync search
      if (this.searchTerm) {
        query.search = this.searchTerm;
      } else {
        delete query.search;
      }

      // Sync page (only if not page 1)
      if (this.currentPage > 1) {
        query.page = this.currentPage.toString();
      } else {
        delete query.page;
      }

      // Sync sort (only if not default)
      if (this.sortBy !== 'dateCreated') {
        query.sortBy = this.sortBy;
      } else {
        delete query.sortBy;
      }

      if (this.sortOrder !== 'desc') {
        query.sortOrder = this.sortOrder;
      } else {
        delete query.sortOrder;
      }

      this.$router.replace({ query });
    },
    getFilterParams() {
      const params = {};

      this.selectedFilters.forEach(filter => {
        const fieldConfig = this.fieldOptions.find(f => f.id === filter.field);
        if (!fieldConfig || !filter.operator) return;

        const fieldType = fieldConfig.type;

        if (fieldType === 'date' || fieldType === 'currency') {
          if (filter.operator === 'between') {
            if (filter.from !== null && filter.from !== undefined && filter.from !== '') {
              params[fieldConfig.apiParams.from] = this.formatParamValue(filter.from, fieldType);
            }
            if (filter.to !== null && filter.to !== undefined && filter.to !== '') {
              params[fieldConfig.apiParams.to] = this.formatParamValue(filter.to, fieldType);
            }
          } else if (filter.operator === 'after' || filter.operator === 'gte') {
            if (filter.from !== null && filter.from !== undefined && filter.from !== '') {
              params[fieldConfig.apiParams.from] = this.formatParamValue(filter.from, fieldType);
            }
          } else if (filter.operator === 'before' || filter.operator === 'lte') {
            if (filter.to !== null && filter.to !== undefined && filter.to !== '') {
              params[fieldConfig.apiParams.to] = this.formatParamValue(filter.to, fieldType);
            }
          } else if (filter.operator === 'is' && fieldType === 'date') {
            if (filter.value !== null && filter.value !== undefined && filter.value !== '') {
              const formattedValue = this.formatParamValue(filter.value, fieldType);
              params[fieldConfig.apiParams.from] = formattedValue;
              params[fieldConfig.apiParams.to] = formattedValue;
            }
          }
        } else if (fieldType === 'select') {
          if (filter.value !== null && filter.value !== undefined) {
            params[fieldConfig.apiParams.param] = filter.value;
          }
        } else if (fieldType === 'multiselect') {
          if (filter.values && filter.values.length > 0 && filter.operator) {
            params[fieldConfig.apiParams.param] = filter.values.join(',');
            params[fieldConfig.apiParams.mode] = filter.operator;
          }
        }
      });

      return params;
    },
    formatParamValue(value, fieldType) {
      if (fieldType === 'currency') {
        // Ensure currency values are integers (cents)
        // MonetaryInput should already provide integers, but ensure it
        return typeof value === 'number' ? value : parseInt(value, 10);
      } else if (fieldType === 'date') {
        // Ensure date is in ISO format (YYYY-MM-DD)
        // DateInput should already provide this format
        return value;
      }
      return value;
    },
    async loadPatients() {
      try {
        this.isLoading = true;

        const offset = (this.currentPage - 1) * this.itemsPerPage;
        const filterParams = this.getFilterParams();

        const response = await queryClient.fetchQuery({
          queryKey: ['patients', {
            limit: this.itemsPerPage,
            offset,
            search: this.searchTerm,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder,
            ...filterParams
          }],
          queryFn: () => api.fetchPatients({
            limit: this.itemsPerPage,
            offset,
            search: this.searchTerm,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder,
            ...filterParams
          })
        });

        this.patients = response.patients;
        this.totalPatients = response.total;

      } catch (err) {
        console.error('Failed to fetch patients:', err);
        alertService.error('Failed to load patients: ' + err.message);
        this.patients = [];
        this.totalPatients = 0;
      } finally {
        this.isLoading = false;
      }
    },
    handleSearchChange(value) {
      this.searchTerm = value;
      this.currentPage = 1;
      this.saveFiltersToLocalStorage();
      this.syncStateToUrl();
      this.loadPatients();
    },
    handleFilterChange(filters) {
      this.selectedFilters = filters;
      this.currentPage = 1;
      this.saveFiltersToLocalStorage();
      this.syncStateToUrl();
      this.loadPatients();
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.syncStateToUrl();
      this.loadPatients();
    },
    handleSort({ sortBy, sortOrder }) {
      this.sortBy = sortBy;
      this.sortOrder = sortOrder;
      this.currentPage = 1;
      this.saveFiltersToLocalStorage();
      this.syncStateToUrl();
      this.loadPatients();
    },
    saveFiltersToLocalStorage() {
      const practiceId = api.getPracticeId();
      if (!practiceId) return;

      try {
        // Store filters as serialized string (same as URL)
        const serialized = serializeFilters(this.selectedFilters);
        if (serialized) {
          localStorage.setItem(`shawncrm_filters_${practiceId}_patients`, serialized);
        } else {
          localStorage.removeItem(`shawncrm_filters_${practiceId}_patients`);
        }

        // Store sort settings
        localStorage.setItem(`shawncrm_sortBy_${practiceId}_patients`, this.sortBy);
        localStorage.setItem(`shawncrm_sortOrder_${practiceId}_patients`, this.sortOrder);
      } catch (err) {
        console.error('Failed to save filters to localStorage:', err);
      }
    },
    loadFiltersFromLocalStorage() {
      const practiceId = api.getPracticeId();
      if (!practiceId) return null;

      try {
        const filtersStr = localStorage.getItem(`shawncrm_filters_${practiceId}_patients`);
        const sortBy = localStorage.getItem(`shawncrm_sortBy_${practiceId}_patients`);
        const sortOrder = localStorage.getItem(`shawncrm_sortOrder_${practiceId}_patients`);

        return {
          selectedFilters: filtersStr ? deserializeFilters(filtersStr) : [],
          sortBy: sortBy || 'dateCreated',
          sortOrder: sortOrder || 'desc'
        };
      } catch (err) {
        console.error('Failed to load filters from localStorage:', err);
        return null;
      }
    },
    loadColumnVisibility() {
      const practiceId = api.getPracticeId();
      if (!practiceId) return;

      try {
        const saved = localStorage.getItem(`shawncrm_columns_${practiceId}_patients`);
        if (saved) {
          const savedVisibility = JSON.parse(saved);
          // Backfill columns added after this user's settings were saved so new
          // columns stay hidden by default instead of appearing for existing users
          this.columns.forEach(col => {
            if (!(col.key in savedVisibility)) {
              savedVisibility[col.key] = false;
            }
          });
          this.columnVisibility = savedVisibility;
        } else {
          // Default columns: Date of Birth, Mobile Phone, Email, Total Spend, Current Balance, Date Last Ticket, Date Created
          const defaultVisibleColumns = ['dateOfBirth', 'mobilePhone', 'email', 'ehrId', 'totalSpend', 'balance', 'dateLastTicket', 'dateCreated'];
          this.columns.forEach(col => {
            this.columnVisibility[col.key] = defaultVisibleColumns.includes(col.key);
          });
        }
      } catch (err) {
        console.error('Failed to load column visibility:', err);
        // Default columns on error
        const defaultVisibleColumns = ['dateOfBirth', 'mobilePhone', 'email', 'totalSpend', 'balance', 'dateLastTicket', 'dateCreated'];
        this.columns.forEach(col => {
          this.columnVisibility[col.key] = defaultVisibleColumns.includes(col.key);
        });
      }
    },
    saveColumnVisibility() {
      const practiceId = api.getPracticeId();
      if (!practiceId) return;

      try {
        localStorage.setItem(
          `shawncrm_columns_${practiceId}_patients`,
          JSON.stringify(this.columnVisibility)
        );
      } catch (err) {
        console.error('Failed to save column visibility:', err);
      }
    },
    handleColumnVisibilityChange(visibility) {
      this.columnVisibility = visibility;
      this.saveColumnVisibility();
    },
    handleCreatePatient() {
      this.$router.push('/patients/new');
    },
    handleRowClick({ rowIndex }) {
      const patient = this.patients[rowIndex];
      if (patient && patient.patientId) {
        this.$router.push(`/patients/${patient.patientId}`);
      }
    },
    getInitials(patient) {
      const firstName = patient.firstName || '';
      const lastName = patient.lastName || '';
      return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || 'U';
    },
    getFullName(patient) {
      const firstName = patient.firstName || '';
      const lastName = patient.lastName || '';
      return `${firstName} ${lastName}`.trim() || 'Unknown';
    },
    getFullAddress(patient) {
      const parts = [];
      if (patient.addressStreet) parts.push(patient.addressStreet);
      if (patient.addressCity) parts.push(patient.addressCity);
      if (patient.addressState) parts.push(patient.addressState);
      if (patient.addressZip) parts.push(patient.addressZip);
      return parts.join(', ');
    },
    // Modal management
    openColumnSettingsModal() {
      this.openModal('columnSettings', {});
    },
    openExportModal() {
      this.openModal('export', {});
    },
    openModal(id, data) {
      this.modal.open = true;
      setTimeout(() => {
        this.modal.id = id;
        this.modal.data = data;
      });
    },
    closeModal() {
      this.modal.id = '';
      setTimeout(() => {
        this.modal.open = false;
      });
    },
    handleModalClick(event) {
      const classList = event.target.classList;
      if (classList.contains('close-on-click')) {
        this.closeModal();
      }
    },
    // Export methods
    async handleExport({ scope, columns }) {
      this.isExporting = true;
      this.exportError = null;
      this.exportProgress = { current: 0, total: 0 };

      await this.$nextTick();
      await new Promise(resolve => setTimeout(resolve, 100));

      try {
        let dataToExport = [];

        if (scope === 'current') {
          dataToExport = this.patients;
          this.exportProgress = { current: this.patients.length, total: this.patients.length };
        } else {
          const filterParams = this.getFilterParams();
          const pageSize = 100;

          const firstPageResponse = await api.fetchPatients({
            limit: pageSize,
            offset: 0,
            search: this.searchTerm,
            sortBy: this.sortBy,
            sortOrder: this.sortOrder,
            ...filterParams
          });

          const totalRecords = firstPageResponse.total;
          const totalPages = Math.ceil(totalRecords / pageSize);

          this.exportProgress = { current: 0, total: totalRecords };
          await this.$nextTick();

          dataToExport = [...firstPageResponse.patients];
          this.exportProgress.current = firstPageResponse.patients.length;
          await this.$nextTick();

          for (let page = 1; page < totalPages; page++) {
            await new Promise(resolve => setTimeout(resolve, 100));

            const offset = page * pageSize;
            const response = await api.fetchPatients({
              limit: pageSize,
              offset,
              search: this.searchTerm,
              sortBy: this.sortBy,
              sortOrder: this.sortOrder,
              ...filterParams
            });

            dataToExport = [...dataToExport, ...response.patients];
            this.exportProgress.current = dataToExport.length;
            await this.$nextTick();
          }
        }

        const csvContent = generateCSV(dataToExport, columns, this.exportConfig);

        const timestamp = getLocalToday();
        const filename = `patients-export-${timestamp}`;
        downloadCSV(csvContent, filename);

        alertService.success(`Successfully exported ${dataToExport.length} patients`);

        // Close modal after short delay to show completion
        setTimeout(() => {
          this.closeModal();
          this.isExporting = false;
          this.exportProgress = { current: 0, total: 0 };
        }, 500);

      } catch (err) {
        console.error('Export failed:', err);
        this.exportError = err.message || 'Failed to export data. Please try again.';
        this.isExporting = false;
      }
    }
  },
  components: {
    AppLayout,
    SearchBar,
    FilterSelector,
    IconCircle,
    CustomTable,
    PaginationControl,
    ExportDataModal,
    ColumnSettingsModal,
    CogIcon
  }
}
</script>

<style scoped>
/* Header section */
.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}

.header-left> :first-child {
  max-width: 300px;
  flex: 1;
  min-width: 200px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.layout-header .button {
  white-space: nowrap;
}

.icon-button {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  min-width: 32px;
}

/* Patient name cell styling */
.patient-name-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  min-width: 0;
}

.name-text {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Cell value styling */
.cell-value {
  color: #333;
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
